<template>
  <div style="height: 100%; position: relative;">
    <DashboardFilterChip :chip-color="chipColor" :chip-text="chipText"></DashboardFilterChip>
    <v-card style="height: 100%">
      <v-card-text>
        <v-row class="mt-3">
          <v-col cols="12" align="center">
            <h1>NTS</h1>
            <br>
            <h2>{{ netTotalSpreadCurrentPeriod | currency }}</h2>
            <br>
            <h2>Month: {{ netTotalSpreadStatusLabel }}</h2>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <area-chart
              :data="lineChartData"
              prefix="$"
              :round="2"
              thousands=","
              loading="Loading..."
              :colors="chartColor"
            ></area-chart>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" align="center">
            <v-btn
              text
              color="primary"
              :disabled="disableButtons"
              @click="showNtsDetails"
            >
              Details
            </v-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" align="end">
            <v-btn
              v-if="!showFetchingDataGIF"
              color="primary"
              outlined
              class="ml-2"
              @click="refreshTile"
              style="float: left;"
              >
                <v-icon>refresh</v-icon>
            </v-btn>
            <i
              v-if="showFetchingDataGIF"
              class="mr-2"
            >
              Fetching Data
            </i>
            <v-progress-circular
              v-if="showFetchingDataGIF"
              :size="30"
              :width="7"
              indeterminate
              color="primary"
            ></v-progress-circular>
            <i
              v-if="lastRefreshTime"
              class="ml-10 mr-5"
            >
              Last Update: {{ lastRefreshTime }}
            </i>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <DynamicModal
      :showModal="showNtsDetailsModal"
      :persistent="false"
      @clickOutside="closeNtsDetails"
      title="NTS Details"
      dialogSize="95%"
    >
      <v-container fluid slot="title-buttons" class="py-1">
        <v-row justify="end">
          <v-btn
            icon
            depressed
            color="primary"
            @click="closeNtsDetails"
          >
            <v-icon color="white">close</v-icon>
          </v-btn>
        </v-row>
      </v-container>
      <v-container fluid slot="content">
        <NtsReportDetails
          :data="currentPeriod.assignmentGrossProfitItems"
          :grossProfitByEmployee="currentPeriod.grossProfitByEmployee"
        ></NtsReportDetails>
      </v-container>
      <v-container fluid slot="buttons" class="pb-0 mb-0">
        <v-row>
          <v-col cols="12">
            <v-btn
              block
              depressed
              outlined
              @click="closeNtsDetails"
            >
              Close
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </DynamicModal>
  </div>
</template>

<script>
import { DynamicModal } from '@/components/dialogs'
import { NtsReportDetails } from '@/components/dashboard/nts/index'
import DashboardFilterChip from '@/components/dashboard/DashboardFilterChip'
import { formatCurrency, getLocalTimeString } from '@/lib/display'
import { getLastDayOfCurrentMonth, getFirstDayOfCurrentMonth } from '@/lib/dates'
import { mapActions } from 'vuex'
import { isJSONString } from '@/lib/miscellaneous'

export default {
  components: {
    DashboardFilterChip,
    DynamicModal,
    NtsReportDetails
  },
  props: {
    businessEntityId: {
      type: Number,
      default: null
    },
    chipText: {
      type: String,
      default: 'All Business Entity'
    },
    chipColor: {
      type: String,
      default: 'primary'
    }
  },
  data () {
    return {
      ntsData: null,
      showNtsDetailsModal: false,
      currentPeriod: { 'netTotalSpread': 0, 'assignments': [] },
      netTotalSpreadCurrentPeriod: 0,
      periodSixMonthsAgo: { 'netTotalSpread': 0, 'assignments': [] },
      lineChartData: {},
      currentPeriodActiveAssignments: [],
      lastRefreshTime: null,
      showFetchingDataGIF: false,
      disableButtons: false,
      dialogSize: '95%'
    }
  },
  watch: {
    ntsData () {
      if (this.ntsData) {
        this.currentPeriod = this.ntsData[this.ntsData.length - 1]
        this.periodSixMonthsAgo = this.ntsData[0]
        this.currentPeriodActiveAssignments = this.currentPeriod.assignmentGrossProfitItems.filter(item => item.activeAssignment)
        this.netTotalSpreadCurrentPeriod = this.currentPeriodActiveAssignments.reduce((accumulator, item) => {
          return accumulator + item.hourlyTotalGrossProfit
        }, 0)
        let chartData = {}
        for (const item of this.ntsData) {
          chartData[item.period] = item.netTotalSpread
        }
        this.lineChartData = chartData
      }
    }
  },
  computed: {
    netTotalSpreadStatus () {
      if (this.currentPeriod &&
        this.currentPeriod.assignmentGrossProfitItems &&
        this.currentPeriod.assignmentGrossProfitItems.length > 0
      ) {
        // Get the month first and last date
        let currentMonthFirstDay = this.getFirstDayOfCurrentMonth()
        let currentMonthLastDay = this.getLastDayOfCurrentMonth()

        // Get the assignments started in the current month
        let assignmentsStartedCurrentMonth = this.currentPeriod.activeAssignments.filter(
          assignment => new Date(assignment.startAt) >= currentMonthFirstDay &&
            new Date(assignment.startAt) <= currentMonthLastDay
        )

        // Get the assignments ended in the current month
        let assignmentsEndedCurrentMonth = this.currentPeriod.activeAssignments.filter(
          assignment => new Date(assignment.endAt) >= currentMonthFirstDay &&
            new Date(assignment.endAt) <= currentMonthLastDay
        )

        let assignmentsStartedNTS = 0
        for (let assignment of assignmentsStartedCurrentMonth) {
          let assignmentGP = this.currentPeriod.assignmentGrossProfitItems.filter(gpItem => gpItem.assignmentId === assignment.id)[0]
          assignmentsStartedNTS += assignmentGP.hourlyTotalGrossProfit
        }

        let assignmentsEndedNTS = 0
        for (let assignment of assignmentsEndedCurrentMonth) {
          let assignmentGP = this.currentPeriod.assignmentGrossProfitItems.filter(gpItem => gpItem.assignmentId === assignment.id)[0]
          assignmentsEndedNTS += assignmentGP.hourlyTotalGrossProfit
        }

        return assignmentsStartedNTS - assignmentsEndedNTS
      }
      return 0
    },
    netTotalSpreadStatusLabel () {
      if (this.netTotalSpreadStatus > 0) {
        return `+${formatCurrency(this.netTotalSpreadStatus)}`
      } else {
        return `-${formatCurrency(this.netTotalSpreadStatus * -1)}`
      }
    },
    chartColor () {
      if (this.currentPeriod.netTotalSpread >= this.periodSixMonthsAgo.netTotalSpread) {
        return ['#80ff80']
      } else {
        return ['#ff6666']
      }
    }
  },
  methods: {
    ...mapActions('dashboard', [
      'getNTS'
    ]),
    getLastDayOfCurrentMonth,
    getFirstDayOfCurrentMonth,
    showNtsDetails () {
      this.showNtsDetailsModal = true
    },
    closeNtsDetails () {
      this.showNtsDetailsModal = false
    },
    refreshTile () {
      this.showFetchingDataGIF = true
      this.disableButtons = true
      this.getNTS(this.businessEntityId).then((data) => {
        if (data && data.length > 0) {
          let dateTimeNow = new Date()
          this.ntsData = data
          localStorage.setItem('dashboard:nts', JSON.stringify(data))
          localStorage.setItem('dashboard:nts-last-refresh-time', JSON.stringify(dateTimeNow.toUTCString()))
          this.lastRefreshTime = getLocalTimeString(dateTimeNow)
        }
        this.showFetchingDataGIF = false
        this.disableButtons = false
      })
    }
  },
  created () {
    if (localStorage.getItem('dashboard:nts') && isJSONString(localStorage.getItem('dashboard:nts'))) {
      this.ntsData = JSON.parse(localStorage.getItem('dashboard:nts'))
    }
    if (localStorage.getItem('dashboard:nts-last-refresh-time') && isJSONString(localStorage.getItem('dashboard:nts-last-refresh-time'))) {
      let lastRefreshTime = new Date(JSON.parse(localStorage.getItem('dashboard:nts-last-refresh-time')))
      this.lastRefreshTime = getLocalTimeString(lastRefreshTime)
    }
    this.refreshTile()
  }
}
</script>
